import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export const EXECUTION_PANEL_INITIAL_SIZE = 32;
export const EXECUTION_PANEL_OPENED_DEFAULT_SIZE = 400;
export const EXECUTION_PANEL_MIN_SIZE = 200;
export const EXECUTION_PANEL_MAX_SIZE = 1000;

export interface DesignState {
  isCapsule: boolean;
  isReadOnly: boolean;
  showShortcutsModal: boolean;
  showMessagesOnCanvas: boolean;
  activeSidePanel: string;
  inspectionModeSizes: [number, number];
  sidePanelSize: number;
  debugPanelSize: number;
  highlightedConnectorId: string | null;
}

export interface DesignActions {
  setIsCapsule: (isCapsule: boolean) => void;
  setIsReadOnly: (isReadOnly: boolean) => void;
  setShowShortcutsModal: (show: boolean) => void;
  setShowMessagesOnCanvas: (show: boolean) => void;
  setActiveSidePanel: (panel: string) => void;
  setInspectionModeSizes: (sizes: [number, number]) => void;
  setSidePanelSize: (sizes: number) => void;
  setDebugPanelSize: (size: number) => void;
  openDebugPanel: () => void;
  closeDebugPanel: () => void;
  setHighlightedConnectorId: (id: string | null) => void;
  toggleShortcutsModal: () => void;
  reset: () => void;
}

const initialState: DesignState = {
  isCapsule: false,
  isReadOnly: false,
  showShortcutsModal: false,
  showMessagesOnCanvas: false,
  activeSidePanel: '',
  inspectionModeSizes: [100, 0],
  sidePanelSize: 0,
  debugPanelSize: EXECUTION_PANEL_INITIAL_SIZE,
  highlightedConnectorId: null
};

export const useDesignUIStore = create<DesignState & DesignActions>()(
  devtools(
    immer(set => ({
      ...initialState,
      setIsCapsule: (isCapsule: boolean) =>
        set(state => {
          state.isCapsule = isCapsule;
        }),
      setIsReadOnly: (isReadOnly: boolean) =>
        set(state => {
          state.isReadOnly = isReadOnly;
        }),
      setShowShortcutsModal: (show: boolean) =>
        set(state => {
          state.showShortcutsModal = show;
        }),
      setShowMessagesOnCanvas: (show: boolean) =>
        set(state => {
          state.showMessagesOnCanvas = show;
        }),
      setActiveSidePanel: (panel: string) =>
        set(state => {
          state.activeSidePanel = panel;
        }),
      setSidePanelSize: (size: number) =>
        set(state => {
          state.sidePanelSize = size;
        }),
      setDebugPanelSize: (size: number) =>
        set(state => {
          state.debugPanelSize = size;
        }),
      setInspectionModeSizes: (sizes: [number, number]) =>
        set({ inspectionModeSizes: sizes }),
      openDebugPanel: () =>
        set({ debugPanelSize: EXECUTION_PANEL_OPENED_DEFAULT_SIZE }),
      closeDebugPanel: () =>
        set({ debugPanelSize: EXECUTION_PANEL_INITIAL_SIZE }),
      setHighlightedConnectorId: (id: string | null) =>
        set(state => {
          state.highlightedConnectorId = id;
        }),
      toggleShortcutsModal: () =>
        set(state => {
          state.showShortcutsModal = !state.showShortcutsModal;
        }),
      reset: () => set(initialState)
    })),
    { name: 'designUI-store' }
  )
);
